import React, { useState, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import isEqual from 'lodash/isEqual';
import { useNavigation } from '@react-navigation/native';
import { usePendingSubmissions } from '@/common/hooks/lecturer/usePendingSubmissions';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import log from '@/common/utils/logger';
import {
  Spinner,
  Box,
} from '@gluestack-ui/themed';
import type { PendingEvaluation } from '@/common/api/models';
import ModuleItem from './ModuleItem';
import { ModuleMenu } from './ModuleMenu';

interface ModuleListProps {
  visible: boolean;
}

interface Module {
  id: string;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  versionHistory?: Record<string, any>;
  state?: { workflowState: string };
}

const ModuleList: React.FC<ModuleListProps> = ({ visible }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();

  // Get modules data from the lecturer data hook
  const { modules, isModulesLoading } = useLecturerModuleData(undefined, undefined, undefined);

  const { data: pendingEvaluations, isLoading: isLoadingPendingEvaluations } = usePendingSubmissions();

  // data for the sidebar
  const [moduleList, setModuleList] = useState<Module[]>([]);
  const [ownPendingEvaluations, setOwnPendingEvaluations] = useState<PendingEvaluation[]>([]);
  const [pendingEvaluationsCount, setPendingEvaluationsCount] = useState<Record<string, number>>({});
  const [totalPendingEvaluations, setTotalPendingEvaluations] = useState(0);

  // Memoize the computation of modules and counts
  const {
    filteredModules,
    filteredPendingEvaluations,
    newPendingEvaluationsCount,
    totalPending,
  } = useMemo(() => {
    if (!modules || !pendingEvaluations) {
      return {
        filteredModules: [],
        filteredPendingEvaluations: [],
        newPendingEvaluationsCount: {},
        totalPending: 0,
      };
    }

    // Filter pending evaluations to only include those from our own modules
    const ownEvals = pendingEvaluations.filter(
      (submission: { moduleId: string }) => modules.some((m: { id: string }) => String(m.id) === String(submission.moduleId)),
    );

    // Calculate pending evaluations count
    const pendingCounts = ownEvals.reduce((count: Record<string, number>, submission: { moduleId: string }) => {
      const { moduleId } = submission;
      const currentCount = count;
      currentCount[moduleId] = (currentCount[moduleId] || 0) + 1;
      return currentCount;
    }, {});

    return {
      filteredModules: modules as Module[],
      filteredPendingEvaluations: ownEvals,
      newPendingEvaluationsCount: pendingCounts,
      totalPending: ownEvals.length,
    };
  }, [modules, pendingEvaluations]);

  // Update state only when values actually change
  useEffect(() => {
    if (!isModulesLoading && !isLoadingPendingEvaluations && modules && pendingEvaluations) {
      if (!isEqual(moduleList, filteredModules)) {
        setModuleList(filteredModules);
      }
      if (!isEqual(ownPendingEvaluations, filteredPendingEvaluations)) {
        setOwnPendingEvaluations(filteredPendingEvaluations);
      }
      if (!isEqual(pendingEvaluationsCount, newPendingEvaluationsCount)) {
        setPendingEvaluationsCount(newPendingEvaluationsCount);
      }
      if (totalPendingEvaluations !== totalPending) {
        setTotalPendingEvaluations(totalPending);
      }
    }
  }, [
    isModulesLoading,
    isLoadingPendingEvaluations,
    filteredModules,
    filteredPendingEvaluations,
    newPendingEvaluationsCount,
    totalPending,
    moduleList,
    ownPendingEvaluations,
    pendingEvaluationsCount,
    totalPendingEvaluations,
    modules,
    pendingEvaluations,
  ]);

  const handleEditModule = (m: Module) => {
    // select version: latest version, not necessarily current
    let targetVersion = '0.0';
    try {
      if (m.versionHistory && Object.keys(m.versionHistory).length > 0) {
        // Get all versions from object keys
        const versions = Object.keys(m.versionHistory);

        // Sort versions by comparing major and minor parts numerically
        versions.sort((a, b) => {
          const [aMajor, aMinor] = a.split('.').map(Number);
          const [bMajor, bMinor] = b.split('.').map(Number);

          // Compare major version first
          if (aMajor !== bMajor) {
            return bMajor - aMajor; // Descending order
          }
          // If major versions are equal, compare minor versions
          return bMinor - aMinor; // Descending order
        });

        // Select the first (latest) version after sorting
        if (versions.length > 0) {
          [targetVersion] = versions;
        }
      }
    } catch (error) {
      log.info('Error getting version history', error);
    }

    navigation.navigate('ModuleEditor', { moduleId: m.id, version: targetVersion, taskId: null });
  };

  if (!visible) return null;

  if (isModulesLoading) {
    return (
      <Box p="$4" alignItems="center">
        <Spinner size="small" />
      </Box>
    );
  }

  return (
    <View>
      {moduleList
        && moduleList
          .sort((a, b) => a.title.localeCompare(b.title))
          .map((m) => (
            <ModuleMenu
              key={m.id}
              module={m}
              handleEditModule={() => handleEditModule(m)}
              pendingEvaluationsCount={pendingEvaluationsCount[m.id]}
              trigger={({ ...triggerProps }: Record<string, unknown>) => (
                <ModuleItem
                  moduleTitle={m.title}
                  pendingEvaluationsCount={pendingEvaluationsCount[m.id]}
                  includesOverdues={ownPendingEvaluations?.some(
                    (submission: { moduleId: string; dueDate: string }) => {
                      const isModuleMatch = submission.moduleId === String(m.id);
                      const isOverdue = new Date(submission.dueDate) < new Date();
                      return isModuleMatch && isOverdue;
                    },
                  )}
                  triggerProps={triggerProps}
                />
              )}
            />
          ))}
    </View>
  );
};

export default ModuleList;
